import React, { useState, useEffect } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, Typography, IconButton, Divider, Chip } from '@mui/material';
import { Close, KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import { headingBlue, red, green, orange, lightGrey, mediumGrey, pureWhite, maroon } from '../../../assets/colors';
import CustomTextField from '../../CustomTextField';
import CustomDropDown from '../../CustomDropDown';
import Loader from '../../Loader';
import HttpService from '../../../backend/requestApis';
import { fetchProgramQuestions } from '../../../backend/backendApis';

const getDifficultyBadge = (difficulty) => {
    const diff = (difficulty || '').toLowerCase();
    let bg = '#eee';
    let fg = '#666';
    if (diff === 'easy') {
        bg = '#e8f5e9';
        fg = '#2e7d32';
    } else if (diff === 'medium') {
        bg = '#fff3e0';
        fg = '#ef6c00';
    } else if (diff === 'hard') {
        bg = '#ffebee';
        fg = '#c62828';
    }

    return (
        <span style={{
            fontSize: '0.7rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            padding: '2px 8px',
            borderRadius: '4px',
            backgroundColor: bg,
            color: fg,
            marginLeft: '8px',
            verticalAlign: 'middle'
        }}>
            {diff}
        </span>
    );
};

const ProgramQuestionsDialog = ({ dialogStatus, updateFunc, programId, programName }) => {
    const [questions, setQuestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    
    // Pagination states
    const [page, setPage] = useState(1);
    const [offset, setOffset] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    // Debounce the search input
    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            setSearchTerm(searchQuery);
        }, 400);

        return () => clearTimeout(delayDebounce);
    }, [searchQuery]);

    // Reset page to 1 when search term changes
    useEffect(() => {
        setPage(1);
    }, [searchTerm]);

    useEffect(() => {
        if (dialogStatus && programId) {
            setIsLoading(true);
            
            let options = {
                url: fetchProgramQuestions,
                method: 'GET',
                params: { 
                    programId: programId,
                    search: searchTerm && searchTerm.trim() ? searchTerm.trim() : undefined,
                    page: page,
                    offset: offset
                },
                headers: {
                    Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
                }
            };

            HttpService(options).then((resp) => {
                const responseData = resp?.data?.data || resp?.data || {};
                const qList = responseData.data || [];
                setQuestions(Array.isArray(qList) ? qList : []);
                setTotalPages(responseData.totalPages || 1);
                setTotalRecords(responseData.totalRecords || 0);
                setIsLoading(false);
            }).catch((err) => {
                console.error("Error fetching program questions: ", err);
                setIsLoading(false);
            });
        }
    }, [dialogStatus, programId, searchTerm, page, offset]);

    // Reset state on open/close
    useEffect(() => {
        if (dialogStatus) {
            setSearchQuery('');
            setSearchTerm('');
            setQuestions([]);
            setPage(1);
            setOffset(10);
            setTotalPages(1);
            setTotalRecords(0);
        }
    }, [dialogStatus]);

    const titleStyle = {
        backgroundColor: headingBlue, 
        color: 'white',
        fontSize: '1rem',
        display: "flex",
        alignItems: 'center',
        padding: "0.5rem 1rem",
        justifyContent: 'space-between'
    };

    return (
        <Dialog maxWidth='md' fullWidth open={dialogStatus} onClose={() => updateFunc(false)}>
            <DialogTitle style={titleStyle}>
                <Typography style={{ fontWeight: 600 }}>Questions Bank: {programName || '-'}</Typography>
                <IconButton onClick={() => updateFunc(false)} style={{ color: 'white', padding: 0 }}>
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent
                sx={{
                    padding: '1.5rem !important',
                    backgroundColor: '#fdfdfd',
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '80vh'
                }}
            >
                {isLoading && <Loader />}
                <Box style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <CustomTextField 
                        placeHolder="Enter Text to Search" 
                        value={searchQuery} 
                        setFunc={setSearchQuery}
                        width="100%"
                    />
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto', pr: '4px', "&::-webkit-scrollbar": { display: 'none' }, scrollbarWidth: "none" }}>
                    {(questions.length === 0 && !isLoading) ? (
                        <Typography style={{ textAlign: 'center', color: '#888', padding: '2rem' }}>
                            No questions found for this program.
                        </Typography>
                    ) : (
                        questions.map((q, idx) => {
                            const displayIdx = ((page - 1) * offset) + idx + 1;
                            return (
                                <Box 
                                    key={q._id || idx} 
                                    style={{ 
                                        padding: '1rem', 
                                        border: `1px solid ${mediumGrey}`, 
                                        borderRadius: '8px', 
                                        marginBottom: '1rem', 
                                        backgroundColor: 'white',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                                    }}
                                >
                                    <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Typography style={{ fontWeight: 600, color: headingBlue, fontSize: '0.95rem' }}>
                                            {displayIdx}. {q.questionText || 'Unknown Question'}
                                        </Typography>
                                        {getDifficultyBadge(q.difficulty)}
                                    </Box>

                                    <Box style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1rem', marginLeft: '0.5rem' }}>
                                        {Array.isArray(q.options) && q.options.map((opt, oIdx) => {
                                            const isCorrect = String(opt).trim().toLowerCase() === String(q.correctAnswer).trim().toLowerCase();
                                            return (
                                                <Box 
                                                    key={oIdx} 
                                                    style={{ 
                                                        padding: '0.4rem 0.8rem', 
                                                        border: `1px solid ${isCorrect ? green : mediumGrey}`, 
                                                        borderRadius: '4px', 
                                                        backgroundColor: isCorrect ? '#e8f5e9' : '#fafafa', 
                                                        display: 'flex', 
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <Typography style={{ fontSize: '0.85rem', color: isCorrect ? 'green' : 'black', fontWeight: isCorrect ? 600 : 400 }}>
                                                        {opt}
                                                    </Typography>
                                                    {isCorrect && (
                                                        <Typography style={{ fontSize: '0.75rem', color: 'green', fontWeight: 600 }}>
                                                            Correct Answer
                                                        </Typography>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                    <Divider style={{ margin: '1rem 0 0.5rem 0' }} />
                                    <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Typography style={{ fontSize: '0.75rem', color: '#888' }}>
                                            Added By: {q.addedByName || '-'} {q.addedByCN ? `(${q.addedByCN})` : ''}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })
                    )}
                </Box>

                {/* Pagination Box */}
                {!isLoading && questions.length > 0 && (
                    <Box style={{ display: 'flex', justifyContent: 'center', padding: "0.5rem 0rem", borderTop: `1px solid ${mediumGrey}`, marginTop: '1rem', alignItems: 'center' }}>
                        <Box style={{ width: "30%", display: 'flex', justifyContent: 'flex-start', alignItems: 'center', padding: "0rem 0.5rem 0rem 0.9rem" }}>
                            <Typography style={{ color: maroon, fontSize: '0.9rem' }}>
                                {((page - 1) * offset) + 1} - {Math.min(page * offset, totalRecords)} of {totalRecords}
                            </Typography>
                        </Box>
                        <Box style={{ width: '40%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <IconButton 
                                size='small' 
                                onClick={() => { if(page > 1) setPage(page - 1) }} 
                                disabled={page === 1}
                            >
                                <KeyboardArrowLeft size='small' sx={{ color: page === 1 ? mediumGrey : headingBlue }} />
                            </IconButton>
                            
                            <Button variant='contained' style={{ minWidth: '0rem', width: '1.5rem', height: '1.5rem', borderRadius: '50%', backgroundColor: headingBlue, color: 'white', padding: '0rem', alignItems: 'center', fontSize: '0.85rem' }}>
                                {page}
                            </Button>
                            
                            <IconButton 
                                size='small' 
                                onClick={() => { if(page < totalPages) setPage(page + 1) }} 
                                disabled={page === totalPages || !totalPages}
                            >
                                <KeyboardArrowRight size='small' sx={{ color: (page === totalPages || !totalPages) ? mediumGrey : headingBlue }} />
                            </IconButton>
                        </Box>
                        <Box style={{ width: '30%', display: 'flex', justifyContent: 'flex-end', padding: "0rem 0.6rem 0rem 0rem" }}>
                            <CustomDropDown 
                                key='table' 
                                value={offset} 
                                width="5rem" 
                                setFunc={(val) => { 
                                    setOffset(typeof val === 'object' ? val.name : val); 
                                    setPage(1); 
                                }} 
                                list={[{name: 10}, {name: 20}, {name: 50}]} 
                                regular={true} 
                            />
                        </Box>
                    </Box>
                )}

                <Box style={{ padding: '1rem 0rem 0rem 0rem', display: 'flex', justifyContent: 'flex-end', borderTop: `1px solid ${mediumGrey}`, marginTop: '1rem' }}>
                    <Button 
                        variant="contained"
                        style={{ backgroundColor: headingBlue, color: 'white', padding: '0.3rem 2rem', textTransform: 'capitalize' }} 
                        onClick={() => updateFunc(false)}
                    >
                        Close
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ProgramQuestionsDialog;
